import {
  Box,
  Typography,
  Paper,
  LinearProgress,
  Chip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { ChecklistPhase, InvestmentChecklistTaskDTO } from '@inversor-hub/types';
import { PHASE_CONFIG } from './checklist-constants';
import { calculatePhaseProgress, getTasksByPhase, canSkipPhase } from './checklist-utils';
import TaskCard from './TaskCard';
import { useState } from 'react';
import { useGetMe } from '@/lib/api/me';
import { ViewList, FilterList, SkipNext } from '@mui/icons-material';
import { useSkipChecklistPhase } from '@/lib/api/investment-checklist';
import { useSnackbar } from 'notistack';

interface PhaseTasksListProps {
  phase: ChecklistPhase;
  tasks: InvestmentChecklistTaskDTO[];
  investmentId: string;
  onPhaseSkipped?: () => void;
}

const PhaseTasksList = ({ phase, tasks, investmentId, onPhaseSkipped }: PhaseTasksListProps) => {
  const config = PHASE_CONFIG[phase];
  const Icon = config.icon;
  const [filterMode, setFilterMode] = useState<'all' | 'mine'>('all');
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const { data: userData } = useGetMe();
  const { mutate: skipPhase, isPending: isSkipping } = useSkipChecklistPhase(investmentId);
  const { enqueueSnackbar } = useSnackbar();

  const phaseTasks = getTasksByPhase(tasks, phase);
  const filteredTasks =
    filterMode === 'mine' && userData
      ? phaseTasks.filter((task) => task.assignedTo === userData.role)
      : phaseTasks;

  const progress = calculatePhaseProgress(tasks, phase);
  const myTasksCount = phaseTasks.filter((task) => task.assignedTo === userData?.role).length;
  const hasPendingTasks = phaseTasks.some((task) => task.status === 'PENDING');

  const handleSkipPhase = () => {
    skipPhase(phase, {
      onSuccess: () => {
        enqueueSnackbar('Fase omitida correctamente', { variant: 'success' });
        setSkipDialogOpen(false);
        onPhaseSkipped?.();
      },
      onError: () => {
        enqueueSnackbar('Error al omitir la fase', { variant: 'error' });
      },
    });
  };

  return (
    <Box>
      {/* Phase Header */}
      <Paper
        sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          borderLeft: 6,
          borderColor: config.color,
          background: `linear-gradient(135deg, ${config.color}10 0%, white 100%)`,
        }}
      >
        <Box
          display="flex"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          gap={2}
          mb={2}
          sx={{ flexDirection: { xs: 'column', sm: 'row' } }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            sx={{
              width: { xs: 52, sm: 64 },
              height: { xs: 52, sm: 64 },
              borderRadius: '50%',
              bgcolor: config.color,
              color: 'white',
              boxShadow: 3,
            }}
          >
            <Icon sx={{ fontSize: { xs: 30, sm: 36 } }} />
          </Box>

          <Box flex={1} minWidth={0}>
            <Typography
              variant="h4"
              fontWeight={700}
              gutterBottom
              sx={{ fontSize: { xs: '1.45rem', sm: '2.125rem' }, lineHeight: 1.2 }}
            >
              {config.label}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {config.description}
            </Typography>
          </Box>

          <Box
            display="flex"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            gap={1}
            sx={{ flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' } }}
          >
            {canSkipPhase(phase) && hasPendingTasks && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<SkipNext />}
                onClick={() => setSkipDialogOpen(true)}
                sx={{ borderColor: 'grey.400', color: 'text.secondary' }}
              >
                Omitir fase
              </Button>
            )}
            <Chip
              label={`${progress.completed}/${progress.total} completadas`}
              sx={{
                bgcolor: config.color,
                color: 'white',
                fontWeight: 600,
                fontSize: '0.875rem',
                px: 1,
              }}
            />
          </Box>
        </Box>

        <Box>
          <LinearProgress
            variant="determinate"
            value={progress.percentage}
            sx={{
              height: 10,
              borderRadius: 2,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                bgcolor: config.color,
                borderRadius: 2,
              },
            }}
          />
          <Typography variant="caption" color="text.secondary" mt={0.5}>
            {progress.percentage}% completado
          </Typography>
        </Box>
      </Paper>

      <Divider sx={{ mb: 3 }} />

      {/* Filter Toggle */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <ToggleButtonGroup
          value={filterMode}
          exclusive
          onChange={(_, value) => value && setFilterMode(value)}
          size="small"
          sx={{
            width: { xs: '100%', sm: 'auto' },
            '& .MuiToggleButton-root': {
              flex: { xs: 1, sm: 'initial' },
              px: { xs: 1, sm: 1.5 },
              whiteSpace: 'nowrap',
            },
          }}
        >
          <ToggleButton value="all">
            <ViewList sx={{ mr: 1 }} fontSize="small" />
            Todas las tareas ({phaseTasks.length})
          </ToggleButton>
          <ToggleButton value="mine">
            <FilterList sx={{ mr: 1 }} fontSize="small" />
            Mis tareas ({myTasksCount})
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Tasks List */}
      {filteredTasks.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {filterMode === 'mine'
              ? 'No tienes tareas asignadas en esta fase'
              : 'No hay tareas en esta fase'}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {filteredTasks.map((task) => (
            <TaskCard key={task.id} task={task} investmentId={investmentId} />
          ))}
        </Box>
      )}

      {/* Skip Phase Confirmation Dialog */}
      <Dialog open={skipDialogOpen} onClose={() => setSkipDialogOpen(false)}>
        <DialogTitle>¿Omitir fase de {config.label}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se marcarán todas las tareas de esta fase como omitidas. Podrás revertir esta acción
            individualmente si es necesario.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipDialogOpen(false)} disabled={isSkipping}>
            Cancelar
          </Button>
          <Button onClick={handleSkipPhase} variant="contained" disabled={isSkipping}>
            Omitir fase
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PhaseTasksList;
